import type { TFileReadyPayload } from '@cascade/api-client/events';
import { useConversations } from '@cascade/react/conversations';
import { useI18n } from '@cascade/react/i18n';
import { useToast } from '@cascade/ui-library';
import type { TFileKey, TFiles } from '@cascade/ui-library';
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  TAttachments,
  TUpload,
  TUploads,
} from './useAttachments.types.ts';

export const useAttachments = (): TAttachments => {
  const { translate, translateError } = useI18n();
  const { toast } = useToast();
  const { uploadFile, removeFile } = useConversations();
  const uploadsRef = useRef<TUploads>(new Map());
  const [uploads, setUploads] = useState<TUploads>(new Map());

  const entries = Array.from(uploads);

  const files = entries.flatMap(([, { file }]) => (file ? [file] : []));

  const loadingKeys = entries
    .filter(([, { file, error }]) => !file && error === undefined)
    .map(([fileKey]) => fileKey);

  const failures = entries.flatMap(([fileKey, { error }]) =>
    error === undefined ? [] : [{ fileKey, error }],
  );

  const isUploading = loadingKeys.length > 0;

  const commit = useCallback(() => {
    setUploads(new Map(uploadsRef.current));
  }, []);

  const deleteUploadedFile = useCallback(
    (fileId: string) => {
      removeFile(fileId).catch(() => undefined);
    },
    [removeFile],
  );

  const deleteRemovedFile = useCallback(
    ({ file_id: fileId, filename }: TFileReadyPayload) => {
      removeFile(fileId).catch((error: unknown) =>
        toast({
          title: translate('agent.attach.deleteFailed', { filename }),
          description: translateError(error),
          variant: 'danger',
        }),
      );
    },
    [removeFile, toast, translate, translateError],
  );

  const upload = useCallback(
    async (fileKey: TFileKey, file: File, started: TUpload) => {
      let settled: TUpload;

      try {
        const uploaded = await uploadFile({ file, filename: file.name });

        settled = { file: uploaded };
      } catch (error) {
        settled = { error };
      }

      const isCurrent = uploadsRef.current.get(fileKey) === started;

      if (!isCurrent) {
        if (settled.file) {
          deleteUploadedFile(settled.file.file_id);
        }

        return;
      }

      uploadsRef.current.set(fileKey, settled);
      commit();
    },
    [uploadFile, deleteUploadedFile, commit],
  );

  const sync = useCallback(
    (picked: TFiles) => {
      const current = uploadsRef.current;

      current.forEach(({ file }, fileKey) => {
        if (picked.has(fileKey)) {
          return;
        }

        current.delete(fileKey);

        if (file) {
          deleteRemovedFile(file);
        }
      });

      picked.forEach((file, fileKey) => {
        if (current.has(fileKey)) {
          return;
        }

        const started: TUpload = {};

        current.set(fileKey, started);
        upload(fileKey, file, started);
      });

      commit();
    },
    [upload, deleteRemovedFile, commit],
  );

  const reset = useCallback(() => {
    uploadsRef.current = new Map();
    commit();
  }, [commit]);

  const discard = useCallback(() => {
    uploadsRef.current.forEach(({ file }) => {
      if (file) {
        deleteUploadedFile(file.file_id);
      }
    });

    reset();
  }, [deleteUploadedFile, reset]);

  useEffect(() => discard, [discard]);

  return { files, loadingKeys, failures, isUploading, sync, reset, discard };
};
